"use client";

import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { Menu } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { CSSProperties } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/** Fixed product behaviour (no CMS fields) */
const LOGO_HOME_HREF = "/";
const APPLY_LEAD_MOBILE_SUFFIX = " /Login";
/**
 * Menu 4 — single mobile bar CTA (replaces stacked Login + Apply).
 * Defaults only; label / background / border are CMS-configurable via the
 * navbar widget settings (mobile_combined_cta_*).
 */
const NAVBAR4_MOBILE_COMBINED_CTA_BG = "#291973";
const NAVBAR4_MOBILE_COMBINED_CTA_BORDER = "rgb(255, 214, 187)";
const NAVBAR4_MOBILE_COMBINED_CTA_LABEL = "Apply/Login";
/** Nav link hover — default only; CMS overrides via nav_link_hover_color */
const NAVBAR4_LINK_HOVER_COLOR = "#DB5803";
/** Fed by every nav link's inline style so the hover utility stays static */
const LINK_HOVER_VAR = "--navbar4-link-hover";

interface MenuItem {
  id: number;
  title: string;
  slug: string;
  url?: string;
  priority: number;
  status: boolean;
  text_color?: string;
  is_button: boolean;
  button_color?: string;
  button_border_color?: string;
  button_font_size?: string;
  is_cta?: boolean;
  children?: MenuItem[];
}

interface RouteItem {
  id?: number;
  route: string;
  label: string;
  nestedRoutes?: {
    label: string;
    route: string;
    textColor?: string;
    isCta?: boolean;
  }[];
  isButton?: boolean;
  /** Top-level nav row (no children): open lead popup instead of Link */
  isCta?: boolean;
  /** Background (solid / lead) */
  buttonColor?: string;
  textColor?: string;
  buttonBorderColor?: string;
  buttonFontSize?: string;
  /** Button row: navigate when true; lead popup when false */
  buttonIsLink?: boolean;
}

/** Bold nav links; hover color from CMS; `text-primary` when active */
function navLinkItemClassName(active: boolean) {
  return cn(
    "px-2 py-1.5 font-semibold hover:!text-[var(--navbar4-link-hover)]",
    active && "text-primary",
  );
}

/**
 * Optional CMS text color / navbar default — only when overriding theme.
 * Always carries the hover color so the hover utility resolves per link.
 */
function navLinkColorOverride(
  textColor: string | undefined,
  navDefaultColor: string | undefined,
  hoverColor: string,
): CSSProperties {
  const hoverVar = { [LINK_HOVER_VAR]: hoverColor } as CSSProperties;
  if (textColor?.trim()) return { ...hoverVar, color: textColor.trim() };
  if (navDefaultColor?.trim())
    return { ...hoverVar, color: navDefaultColor.trim() };
  return hoverVar;
}

function pickMenuItemStr(
  item: MenuItem & Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function pickMenuItemBool(
  item: MenuItem & Record<string, unknown>,
  ...keys: string[]
): boolean {
  for (const k of keys) {
    const v = item[k];
    if (v === true || v === 1) return true;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "1" || s === "yes") return true;
    }
  }
  return false;
}

function resolvePath(item: MenuItem): string {
  return item.url && item.url.trim() !== "" ? item.url.trim() : `/${item.slug}`;
}

/** API / MySQL may send snake_case or camelCase */
function pickSectionStr(
  raw: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function pickStickyToTop(raw: Record<string, unknown>): boolean {
  const v = raw.sticky_to_top ?? raw.stickyToTop;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
}

/** next/image remotePatterns often omit CMS/CDN hosts — bypass optimizer for absolute URLs */
function shouldUnoptimizeImageSrc(src: string): boolean {
  return /^https?:\/\//i.test(src.trim());
}

const NavBar4 = ({ data }: { data: any }) => {
  const raw = data as Record<string, unknown> | undefined;
  const isPreview = Boolean(raw?.isPreview ?? raw?.is_preview);
  /** Public API sends flat payload; admin preview may wrap `{ data, isPreview }` */
  const sectionData = (raw?.data ?? raw) as Record<string, unknown> | undefined;
  const pathname = usePathname();
  const dispatch = useDispatch();

  if (!sectionData || typeof sectionData !== "object") {
    return null;
  }

  const rawSection = sectionData as Record<string, unknown>;

  const {
    background_color = "#ffffff",
    menuItems = [],
    nav_link_color,
  } = sectionData as any;

  const hamburger_icon = pickSectionStr(
    rawSection,
    "hamburger_icon",
    "hamburgerIcon",
  );
  /* Hamburger visibility. Defaults TRUE — a missing or NULL value must keep
     the hamburger, so only an explicit editor opt-out ever hides it. */
  const rawShowHamburger = rawSection.show_hamburger ?? rawSection.showHamburger;
  const showHamburger = !(
    rawShowHamburger === false ||
    rawShowHamburger === 0 ||
    rawShowHamburger === "0"
  );
  const hamburger_icon_alt = pickSectionStr(
    rawSection,
    "hamburger_icon_alt",
    "hamburgerIconAlt",
  );
  const logo_icon = pickSectionStr(rawSection, "logo_icon", "logoIcon");
  const logo_icon_alt = pickSectionStr(rawSection, "logo_icon_alt", "logoIconAlt");
  const mobile_logo_icon = pickSectionStr(
    rawSection,
    "mobile_logo_icon",
    "mobileLogoIcon",
  );
  const mobile_logo_icon_alt = pickSectionStr(
    rawSection,
    "mobile_logo_icon_alt",
    "mobileLogoIconAlt",
  );
  const mobile_hamburger_background = pickSectionStr(
    rawSection,
    "mobile_hamburger_background",
    "mobileHamburgerBackground",
  );
  const mobile_menu_background = pickSectionStr(
    rawSection,
    "mobile_menu_background",
    "mobileMenuBackground",
  );
  const nav_link_hover_color = pickSectionStr(
    rawSection,
    "nav_link_hover_color",
    "navLinkHoverColor",
  );
  const mobile_combined_cta_label = pickSectionStr(
    rawSection,
    "mobile_combined_cta_label",
    "mobileCombinedCtaLabel",
  );
  const mobile_combined_cta_background = pickSectionStr(
    rawSection,
    "mobile_combined_cta_background",
    "mobileCombinedCtaBackground",
  );
  const mobile_combined_cta_border_color = pickSectionStr(
    rawSection,
    "mobile_combined_cta_border_color",
    "mobileCombinedCtaBorderColor",
  );

  /** Menu Item 4 — public pages may omit this until re-saved in admin */
  const resolvedMobileHamburgerBg =
    mobile_hamburger_background?.trim() || "#291973";

  const resolvedLinkHoverColor =
    nav_link_hover_color?.trim() || NAVBAR4_LINK_HOVER_COLOR;

  /** Mobile combined Apply/Login CTA — CMS overrides with widget-setting fallbacks */
  const resolvedMobileCombinedCtaLabel =
    mobile_combined_cta_label?.trim() || NAVBAR4_MOBILE_COMBINED_CTA_LABEL;
  const resolvedMobileCombinedCtaBg =
    mobile_combined_cta_background?.trim() || NAVBAR4_MOBILE_COMBINED_CTA_BG;
  const resolvedMobileCombinedCtaBorder =
    mobile_combined_cta_border_color?.trim() ||
    NAVBAR4_MOBILE_COMBINED_CTA_BORDER;

  const stickyNav = pickStickyToTop(rawSection);

  const logoMobile = mobile_logo_icon || logo_icon;
  const logoDesktop = logo_icon || mobile_logo_icon;
  const logoAlt = logo_icon_alt || mobile_logo_icon_alt || "logo";

  const routes: RouteItem[] = useMemo(() => {
    if (!menuItems?.length) return [];

    const activeItems = menuItems
      .filter((item: MenuItem) => item.status)
      .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority);

    return activeItems.map((item: MenuItem) => {
      const explicitUrl = item.url && item.url.trim() !== "";
      const path = resolvePath(item);
      const rawItem = item as MenuItem & Record<string, unknown>;
      const isCta = pickMenuItemBool(rawItem, "is_cta", "isCta");

      if (item.is_button) {
        const raw = rawItem;
        return {
          id: item.id,
          route: path,
          label: item.title,
          isButton: true,
          buttonColor: pickMenuItemStr(raw, "button_color", "buttonColor"),
          textColor: pickMenuItemStr(raw, "text_color", "textColor"),
          buttonBorderColor: pickMenuItemStr(
            raw,
            "button_border_color",
            "buttonBorderColor",
          ),
          buttonFontSize: pickMenuItemStr(
            raw,
            "button_font_size",
            "buttonFontSize",
          ),
          buttonIsLink: explicitUrl && !isCta,
        };
      }

      const route: RouteItem = {
        id: item.id,
        route: path,
        label: item.title,
        textColor: item.text_color,
        isCta,
      };

      if (item.children?.length) {
        const activeChildren = item.children
          .filter((child: MenuItem) => child.status)
          .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority);

        route.nestedRoutes = activeChildren.map((child: MenuItem) => {
          const rawChild = child as MenuItem & Record<string, unknown>;
          return {
            label: child.title,
            route: resolvePath(child),
            textColor: child.text_color,
            isCta: pickMenuItemBool(rawChild, "is_cta", "isCta"),
          };
        });
      }

      return route;
    });
  }, [menuItems]);

  const menuRoutes = routes.filter((r) => !r.isButton);
  const buttonRoutes = routes.filter((r) => r.isButton);

  const openNavLeadPopup = useCallback(() => {
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [dispatch]);

  const activePath = menuRoutes.find((r) => r.route === pathname)?.route;
  let activeNestedPath: string | undefined;

  if (!activePath) {
    menuRoutes.find((route) => {
      const hit = route.nestedRoutes?.some((nested) => {
        if (nested.route === pathname || `/${nested.route}` === pathname) {
          return true;
        }
        return false;
      });
      if (hit) {
        activeNestedPath = route.route;
        return true;
      }
      return false;
    });
  }

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [navBottom, setNavBottom] = useState(0);

  useEffect(() => {
    if (mobileOpen && barRef.current) {
      setNavBottom(barRef.current.getBoundingClientRect().bottom);
    }
  }, [mobileOpen]);

  /** Prevent background scroll while mobile sheet is open.
   *  Note: We intentionally do NOT set html.style.overflow = "hidden" here.
   *  Doing so breaks position:sticky on iOS Safari when the page is scrolled —
   *  the sticky navbar snaps back to its document-flow position (above the
   *  viewport), making the top bar disappear behind the drawer.
   *  body overflow:hidden + touchAction:none is sufficient on modern browsers,
   *  and the drawer's overscroll-contain stops momentum from escaping. */
  useEffect(() => {
    if (!mobileOpen) return;
    const body = document.body;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyTouch = body.style.touchAction;
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    return () => {
      body.style.overflow = prevBodyOverflow;
      body.style.touchAction = prevBodyTouch;
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  /** Close the mobile card when tapping outside it (the hamburger lives in barRef → its own toggle still wins) */
  useEffect(() => {
    if (!mobileOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !sheetRef.current?.contains(target) &&
        !barRef.current?.contains(target)
      ) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [mobileOpen]);

  /** Publishes bar height for downstream sticky offsets */
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) {
      document.documentElement.style.removeProperty("--nav4-sticky-height");
      return;
    }
    const setVar = () => {
      document.documentElement.style.setProperty(
        "--nav4-sticky-height",
        `${el.offsetHeight}px`,
      );
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--nav4-sticky-height");
    };
  }, [routes, menuItems, stickyNav, mobileOpen]);

  const logoHref = LOGO_HOME_HREF;

  if (!routes.length && !logo_icon && !mobile_logo_icon) {
    return null;
  }

  const ctaBase =
    "focus-visible:ring-ring items-center justify-center whitespace-nowrap rounded-md font-semibold transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50 shrink-1 px-4 py-1 min-[1024px]:px-5";

  const ctaTypography = (route: RouteItem) =>
    route.buttonFontSize?.trim()
      ? ""
      : "!text-body-3 text-xs min-[1024px]:!text-base";

  const ctaInlineStyle = (
    route: RouteItem,
    variant: "outline" | "solid" | "lead",
  ): CSSProperties => {
    const s: CSSProperties = {};
    if (route.textColor?.trim()) {
      s.color = route.textColor;
    } else if (variant !== "outline") {
      s.color = "#ffffff";
    }
    if (route.buttonColor?.trim()) s.backgroundColor = route.buttonColor;
    if (route.buttonBorderColor?.trim()) {
      s.borderColor = route.buttonBorderColor;
      s.borderWidth = 1;
      s.borderStyle = "solid";
    }
    if (route.buttonFontSize?.trim()) s.fontSize = route.buttonFontSize;
    return s;
  };

  const renderCta = (route: RouteItem, mode: "bar" | "sheet") => {
    const solidLink = Boolean(route.buttonColor?.trim());

    const leadSuffix =
      !route.buttonIsLink ? (
        mode === "bar" ? (
          <span className="min-[768px]:hidden">{APPLY_LEAD_MOBILE_SUFFIX}</span>
        ) : (
          <span>{APPLY_LEAD_MOBILE_SUFFIX}</span>
        )
      ) : null;

    if (route.buttonIsLink) {
      const customBorder = Boolean(route.buttonBorderColor?.trim());
      return (
        <Link
          href={route.route}
          className={cn(
            ctaBase,
            ctaTypography(route),
            "inline-flex max-w-full min-w-0",
            solidLink
              ? cn(
                  "cursor-pointer border",
                  !customBorder && "border-primary-light-200",
                  !route.buttonColor?.trim() && "bg-secondary text-white min-[768px]:bg-primary",
                )
              : cn(
                  "border bg-transparent",
                  !route.textColor?.trim() && "text-primary",
                  !customBorder && "border-primary-light-400",
                ),
            mode === "bar" &&
              "max-w-[min(100%,11rem)] px-3 py-1 !text-xs min-[1024px]:max-w-none min-[1024px]:px-5 min-[1024px]:py-1 min-[1024px]:!text-base",
            customBorder && "border-solid",
          )}
          style={ctaInlineStyle(route, solidLink ? "solid" : "outline")}
          onClick={() => mobileOpen && setMobileOpen(false)}
        >
          {route.label}
        </Link>
      );
    }

    const customBorder = Boolean(route.buttonBorderColor?.trim());
    return (
      <button
        type="button"
        className={cn(
          ctaBase,
          ctaTypography(route),
          "inline-flex max-w-full min-w-0 cursor-pointer border",
          !customBorder && "border-primary-light-200",
          !route.buttonColor?.trim() && "bg-secondary text-white min-[768px]:bg-primary",
          mode === "bar" &&
            "max-w-[min(100%,11rem)] px-3 py-1 !text-xs min-[1024px]:max-w-none min-[1024px]:px-5 min-[1024px]:py-1 min-[1024px]:!text-base",
          customBorder && "border-solid",
        )}
        style={ctaInlineStyle(route, "lead")}
        onClick={() => {
          if (mobileOpen) setMobileOpen(false);
          openNavLeadPopup();
        }}
      >
        {route.label}
        {leadSuffix}
      </button>
    );
  };

  const renderMobileCombinedBarCta = (btnRoutes: RouteItem[]) => {
    if (!btnRoutes.length) return null;
    const anyLead = btnRoutes.some((r) => !r.buttonIsLink);
    const firstLink = btnRoutes.find((r) => r.buttonIsLink);
    const mobileCombinedCls = cn(
      "inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-solid",
      "px-6 py-1 text-center font-poppins !text-base !font-medium !leading-normal tracking-normal !text-white",
      "max-w-[min(100%,16rem)] min-w-0",
      "transition-[color,background-color,border-color,text-decoration-color,fill,stroke] duration-150 ease-in-out",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
      "[-webkit-tap-highlight-color:transparent]",
    );
    const mobileCombinedStyle: CSSProperties = {
      backgroundColor: resolvedMobileCombinedCtaBg,
      borderColor: resolvedMobileCombinedCtaBorder,
      color: "#ffffff",
    };
    if (anyLead) {
      return (
        <button
          type="button"
          className={mobileCombinedCls}
          style={mobileCombinedStyle}
          onClick={() => {
            setMobileOpen(false);
            openNavLeadPopup();
          }}
        >
          {resolvedMobileCombinedCtaLabel}
        </button>
      );
    }
    if (firstLink) {
      return (
        <Link
          href={firstLink.route}
          className={mobileCombinedCls}
          style={mobileCombinedStyle}
          onClick={() => setMobileOpen(false)}
        >
          {resolvedMobileCombinedCtaLabel}
        </Link>
      );
    }
    return null;
  };

  return (
    <>
      <div
        ref={barRef}
        className={cn(
          "box-border flex w-full items-center justify-between gap-3 bg-white px-4 font-semibold",
          /* `position: relative` and `sticky` must not both apply — last Tailwind utility wins and breaks sticky */
          stickyNav ? "sticky z-[90]" : "relative z-10",
          /* Mobile: 66px fixed height, edge-to-edge — a max-width cap here leaves
             side gaps on any phone wider than the cap (390/412/430px devices) */
          "max-[1023px]:h-[66px] max-[1023px]:min-h-[66px] min-[1024px]:h-auto min-[1024px]:min-h-0 min-[1024px]:px-9 min-[1024px]:py-2.5",
        )}
        style={{
          backgroundColor: background_color,
          ...(stickyNav
            ? {
                top: "var(--header4-sticky-height, 0px)",
              }
            : {}),
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3 min-[640px]:gap-6">
          {showHamburger && (
          <button
            type="button"
            className={cn(
              "focus-visible:ring-ring flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-md p-2 text-xs font-medium text-white transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 min-[1024px]:hidden",
            )}
            style={{ backgroundColor: resolvedMobileHamburgerBg }}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {hamburger_icon ? (
              <Image
                src={hamburger_icon}
                alt={hamburger_icon_alt || "Menu"}
                width={24}
                height={24}
                className="h-[24px] w-[24px] shrink-0 object-contain"
                unoptimized={shouldUnoptimizeImageSrc(hamburger_icon)}
              />
            ) : (
              <Menu className="h-[24px] w-[24px] shrink-0 stroke-white stroke-[2px]" />
            )}
          </button>
          )}

          <Link
            className="flex min-w-0 shrink-0 flex-1 items-center justify-center min-[1024px]:flex-initial min-[1024px]:justify-start"
            href={logoHref}
            data-cta-name="logo"
          >
            {logoMobile ? (
              <Image
                alt={logoAlt}
                loading="lazy"
                width={111.62}
                height={26.62}
                className="min-[1024px]:hidden w-[111.62px] h-[26.62px] object-contain"
                src={logoMobile}
              />
            ) : null}
            {logoDesktop ? (
              <Image
                alt={logoAlt}
                loading="lazy"
                width={180}
                height={42.25}
                className="hidden min-[1024px]:block w-[180px] h-[42.25px] object-contain"
                src={logoDesktop}
              />
            ) : null}
          </Link>
        </div>

        {/* Menu Item 4: nav links + CTAs grouped on the right, logo alone on the left */}
        <div className="ml-auto flex min-w-0 items-center justify-end gap-3 min-[640px]:gap-4 min-[1024px]:gap-6">
          <nav
            ref={dropdownRef}
            className="relative hidden min-w-0 items-center justify-end gap-3 text-sm min-[1024px]:flex"
          >
            {menuRoutes.map((route, routeIndex) =>
              route.nestedRoutes?.length ? (
                <div key={`${route.route}-${routeIndex}`} className="relative">
                  <button
                    type="button"
                    data-menu-button="true"
                    data-cta-name="menu_link"
                    className={cn(
                      "flex items-center gap-1",
                      navLinkItemClassName(
                        activeNestedPath === route.route ||
                          activePath === route.route,
                      ),
                    )}
                    style={navLinkColorOverride(
                      route.textColor,
                      nav_link_color,
                      resolvedLinkHoverColor,
                    )}
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === route.route ? null : route.route,
                      )
                    }
                  >
                    {route.label}
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={cn(
                        "size-4",
                        openDropdown === route.route && "rotate-180",
                      )}
                      aria-hidden
                    >
                      <path
                        d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z"
                        fill="currentColor"
                        fillRule="evenodd"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {openDropdown === route.route && (
                    <div
                      className="absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-md bg-white py-2 shadow-md"
                      style={{ backgroundColor: "#FFFFFF" }}
                      role="menu"
                    >
                      {route.nestedRoutes.map((nested, nestedIndex) => {
                        const nestedActive =
                          pathname === nested.route ||
                          pathname === nested.route.replace(/\/$/, "");
                        const nestedClass = cn(
                          "block px-3 py-2 text-sm font-semibold hover:!text-[var(--navbar4-link-hover)]",
                          nestedActive && "text-primary",
                        );
                        const nestedStyle = navLinkColorOverride(
                          nested.textColor,
                          nav_link_color,
                          resolvedLinkHoverColor,
                        );
                        if (nested.isCta) {
                          return (
                            <button
                              key={`${nested.route}-${nestedIndex}`}
                              type="button"
                              data-cta-name="menu_link"
                              className={cn(nestedClass, "w-full text-left")}
                              style={nestedStyle}
                              onClick={() => {
                                setOpenDropdown(null);
                                openNavLeadPopup();
                              }}
                            >
                              {nested.label}
                            </button>
                          );
                        }
                        return (
                          <Link
                            key={`${nested.route}-${nestedIndex}`}
                            href={nested.route}
                            className={nestedClass}
                            style={nestedStyle}
                            data-cta-name="menu_link"
                            onClick={() => setOpenDropdown(null)}
                          >
                            {nested.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div key={`${route.route}-${routeIndex}`} className="relative">
                  {route.isCta ? (
                    <button
                      type="button"
                      data-cta-name="menu_link"
                      className={cn(
                        "block w-full cursor-pointer bg-transparent p-0 text-left font-semibold",
                        navLinkItemClassName(false),
                      )}
                      style={navLinkColorOverride(
                        route.textColor,
                        nav_link_color,
                        resolvedLinkHoverColor,
                      )}
                      onClick={openNavLeadPopup}
                    >
                      {route.label}
                    </button>
                  ) : (
                    <Link
                      href={route.route}
                      data-cta-name="menu_link"
                      className={cn(
                        "block",
                        navLinkItemClassName(activePath === route.route),
                      )}
                      style={navLinkColorOverride(
                        route.textColor,
                        nav_link_color,
                        resolvedLinkHoverColor,
                      )}
                    >
                      {route.label}
                    </Link>
                  )}
                </div>
              ),
            )}
          </nav>

          <div
            className={cn(
              "flex min-w-0 max-w-[48%] shrink-0 flex-wrap items-center justify-end gap-1.5 min-[640px]:gap-2 min-[1024px]:max-w-none min-[1024px]:gap-[10px]",
            )}
          >
          {buttonRoutes.length > 0 ? (
            <>
              {buttonRoutes.map((route) => (
                <span
                  key={route.id ?? `${route.route}-${route.label}`}
                  className="max-[1023px]:hidden"
                >
                  {renderCta(route, "bar")}
                </span>
              ))}
              <span className="hidden max-[1023px]:inline-flex">
                {renderMobileCombinedBarCta(buttonRoutes)}
              </span>
            </>
          ) : null}
          </div>
        </div>
      </div>

      {/* Mobile menu — left-anchored white card that slides in/out under the bar (matches NavBar5/IITKGP).
          Kept mounted in the live view so it can animate out (right→left) on close. */}
      {(mobileOpen || !isPreview) && (
        <div
          ref={sheetRef}
          aria-hidden={!isPreview && !mobileOpen}
          className={cn(
            "z-40 flex w-64 max-w-[85vw] flex-col gap-2 overflow-y-auto overscroll-contain rounded-md border border-gray-100 p-4 shadow-lg min-[1024px]:hidden",
            !mobile_menu_background?.trim() && "bg-[#FFFFFF]",
            isPreview
              ? "relative max-h-[70vh]"
              : cn(
                  "fixed left-0 transition-transform duration-300 ease-in-out",
                  mobileOpen ? "translate-x-0" : "-translate-x-full",
                ),
          )}
          style={{
            ...(isPreview
              ? {}
              : {
                  top: navBottom || undefined,
                  maxHeight: navBottom
                    ? `calc(100vh - ${navBottom}px)`
                    : undefined,
                }),
            ...(mobile_menu_background?.trim() ?
              { backgroundColor: mobile_menu_background.trim() }
            : {}),
          }}
        >
          {menuRoutes.map((route, routeIndex) =>
            route.nestedRoutes?.length ? (
              <div key={`${route.route}-${routeIndex}`} className="w-full">
                <button
                  type="button"
                  data-cta-name="menu_link"
                  className="flex w-full items-center justify-between py-2 text-left font-semibold"
                  onClick={() =>
                    setMobileAccordion(
                      mobileAccordion === route.route ? null : route.route,
                    )
                  }
                >
                  <span
                    className={navLinkItemClassName(
                      activeNestedPath === route.route ||
                        activePath === route.route,
                    )}
                    style={navLinkColorOverride(
                      route.textColor,
                      nav_link_color,
                      resolvedLinkHoverColor,
                    )}
                  >
                    {route.label}
                  </span>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    className={cn(
                      "size-4 transition-transform",
                      mobileAccordion === route.route && "rotate-180",
                    )}
                    aria-hidden
                  >
                    <path
                      d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z"
                      fill="currentColor"
                      fillRule="evenodd"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {mobileAccordion === route.route && (
                  <div className="flex flex-col border-l pl-3 mt-1 gap-2">
                    {route.nestedRoutes.map((nested, nestedIndex) => {
                      const nestedActive =
                        pathname === nested.route ||
                        pathname === nested.route.replace(/\/$/, "");
                      const nestedClass = cn(
                        "block py-1.5 text-base font-semibold hover:!text-[var(--navbar4-link-hover)]",
                        nestedActive && "text-primary",
                      );
                      const nestedStyle = navLinkColorOverride(
                        nested.textColor,
                        nav_link_color,
                        resolvedLinkHoverColor,
                      );
                      if (nested.isCta) {
                        return (
                          <button
                            key={`${nested.route}-${nestedIndex}`}
                            type="button"
                            data-cta-name="menu_link"
                            className={cn(
                              nestedClass,
                              "w-full cursor-pointer bg-transparent text-left",
                            )}
                            style={nestedStyle}
                            onClick={() => {
                              setMobileOpen(false);
                              openNavLeadPopup();
                            }}
                          >
                            {nested.label}
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={`${nested.route}-${nestedIndex}`}
                          href={nested.route}
                          className={nestedClass}
                          style={nestedStyle}
                          data-cta-name="menu_link"
                          onClick={() => setMobileOpen(false)}
                        >
                          {nested.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : route.isCta ? (
              <button
                key={`${route.route}-${routeIndex}`}
                type="button"
                data-cta-name="menu_link"
                className={cn(
                  "block w-full cursor-pointer bg-transparent py-2 text-left text-base font-semibold",
                  navLinkItemClassName(false),
                )}
                style={navLinkColorOverride(route.textColor, nav_link_color, resolvedLinkHoverColor)}
                onClick={() => {
                  setMobileOpen(false);
                  openNavLeadPopup();
                }}
              >
                {route.label}
              </button>
            ) : (
              <Link
                key={`${route.route}-${routeIndex}`}
                href={route.route}
                data-cta-name="menu_link"
                className={cn(
                  "block text-base",
                  navLinkItemClassName(activePath === route.route),
                )}
                style={navLinkColorOverride(
                  route.textColor,
                  nav_link_color,
                  resolvedLinkHoverColor,
                )}
                onClick={() => setMobileOpen(false)}
              >
                {route.label}
              </Link>
            ),
          )}
        </div>
      )}
    </>
  );
};

export default NavBar4;
